import fetch from 'cross-fetch';
import {ThrottleConfig,error} from '../toast'
const DOMAIN = 'https://scratch.hangshuo.site';
// const DOMAIN = 'http://127.0.0.1:8081';
const TENANT_ID = 1008;
const CONTEXT_PATH = 'api';

export const API_DOMAIN = `${DOMAIN}/${CONTEXT_PATH}`;

// ---------- Token 操作 ----------
export const getToken = () => localStorage.getItem('token');
export const setToken = token => localStorage.setItem('token', token);
export const getRefreshToken = () => localStorage.getItem('refresh-token');
export const setRefreshToken = token => localStorage.setItem('refresh-token', token);
export const removeToken = () => {
    localStorage.removeItem('token');
    localStorage.removeItem('refresh-token');
};

// 公共请求头
const getDefaultHeaders = ({json = true} = {}) => ({
    ...(json ? {'Content-Type': 'application/json'} : {}),
    'Tenant-Id': TENANT_ID,
    ...(getToken() ? {Authorization: `Bearer ${getToken()}`} : {})
});

// 拼接查询参数
const isAbsoluteUrl = url => /^https?:\/\//i.test(url);

const buildUrl = (url, params) => {
    if (!params) return url;
    const query = new URLSearchParams(params).toString();
    return query ? `${url}?${query}` : url;
};

const resolveRequestUrl = url => (isAbsoluteUrl(url) ? url : API_DOMAIN + url);

// 并发控制变量
let isRefreshing = false;
// 存储等待队列，每个元素是 { resolve, reject }，用于在刷新完成后重试或拒绝
let refreshSubscribers = [];

// 刷新成功：用新 token 重试所有等待的请求
const onRefreshed = newToken => {
    refreshSubscribers.forEach(({resolve}) => resolve(newToken));
    refreshSubscribers = [];
};

// 刷新失败：拒绝所有等待的请求
const onRefreshedFailed = error => {
    refreshSubscribers.forEach(({reject}) => reject(error));
    refreshSubscribers = [];
};

let refreshTokenAPI = null;
let request = null;

const retryAfterTokenRefresh = (url, method, data, options) => {
    const retryRequest = new Promise((resolve, reject) => {
        refreshSubscribers.push({
            resolve: () => {
                request(url, method, data, {...options, _retry: true})
                    .then(resolve)
                    .catch(reject);
            },
            reject
        });
    });

    if (!isRefreshing) {
        isRefreshing = true;
        refreshTokenAPI()
            .then(newToken => {
                onRefreshed(newToken);
                isRefreshing = false;
            })
            .catch(refreshError => {
                removeToken();
                onRefreshedFailed(refreshError);
                isRefreshing = false;
            });
    }

    return retryRequest;
};

// ---------- 核心请求函数 ----------
request = (url, method = 'GET', data = null, options = {}) => {
    const {
        _retry = false,
        _skipRefresh = false,
        rawBody = false,
        headers: optionHeaders = {},
        ...fetchOptions
    } = options;
    const isFormData = typeof FormData !== 'undefined' && data instanceof FormData;
    const headers = {...getDefaultHeaders({json: !isFormData}), ...optionHeaders};
    const config = {
        method,
        credentials: 'include',
        headers,
        mode: 'cors',
        ...fetchOptions
    };

    if (data && ['POST', 'PUT', 'PATCH'].includes(method.toUpperCase())) {
        config.body = isFormData || rawBody ? data : JSON.stringify(data);
    }

    return fetch(resolveRequestUrl(url), config)
        .catch(networkError => {
            error("网络连接失败，请检查网络环境",ThrottleConfig.NetError)
            throw new Error(`Network error: ${networkError.message}`);
        })
        .then(response => {
            // ---------- 处理 401 并自动刷新（除非是刷新请求本身或已重试） ----------
            const contentType = response.headers.get('content-type');
            const isJson = contentType && contentType.includes('application/json');
            const responseDataPromise = isJson ?
                response.json().catch(() => ({})) :
                response.text();

            return responseDataPromise.then(responseData => {
                const isTokenExpired = response.status === 401 ||
                    (response.ok && responseData && responseData.code === 401);

                if (isTokenExpired && !_retry && !_skipRefresh) {
                    // 先把当前失败请求加入队列，再触发刷新；否则首个 401 请求会错过刷新完成通知。
                    return retryAfterTokenRefresh(url, method, data, options);
                }
                if (isTokenExpired) {
                    const error = new Error((responseData && responseData.message) || 'Unauthorized');
                    error.status = 401;
                    error.data = responseData;
                    throw error;
                }

                // 成功响应
                return responseData;
            });
        });
};

// ---------- 刷新令牌 API ----------
// 注意：这个函数内部调用 request 时必须跳过自动刷新（_skipRefresh: true）
refreshTokenAPI = () => {
    const refreshToken = getRefreshToken();
    if (!refreshToken) {
        error("登录失效，请重新登录",ThrottleConfig.TokenInvalid)
        return Promise.reject(new Error('登录失效，请重新登录'));
    }

    // 使用 _skipRefresh 标记防止递归
    return request('/refresh-token', 'POST', {refreshToken}, {_skipRefresh: true})
        .then(response => {
            // 这里 response 是成功解析的 JSON（因为 request 在非 2xx 时会抛错）
            if (response.success) {
                const {accessToken, refreshToken: newRefreshToken} = response.result;
                setToken(accessToken);
                setRefreshToken(newRefreshToken);
                const userEvent = new CustomEvent("tokenRefresh", {detail: {}});
                window.dispatchEvent(userEvent);
                return accessToken;
            }
            error("登录失效，请重新登录",ThrottleConfig.TokenInvalid)
            throw new Error(response.message);
        });
};

// 导出便捷方法
export const http = {
    get: (url, params, options) =>
        request(buildUrl(url, params), 'GET', null, options),
    post: (url, data, options) => request(url, 'POST', data, options),
    put: (url, data, options) => request(url, 'PUT', data, options),
    patch: (url, data, options) => request(url, 'PATCH', data, options),
    delete: (url, params, options) =>
        request(buildUrl(url, params), 'DELETE', null, options),
    request,
    tenantId: TENANT_ID,
    domain: DOMAIN,
    contextPath: CONTEXT_PATH
};
